import { useEffect, useState } from "react";
import { ExternalLink, ImagePlus, Loader2, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { compressSquareImage, dataUrlKb } from "@/lib/image";
import { Confirm } from "@/components/Common";
import { Field } from "@/components/ProfileFields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PageTitle, Panel } from "./AdminLayout";

const EMPTY = { title: "", description: "", link: "", image: "" };

function ImagePicker({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const pick = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("File harus berupa gambar.");
    setBusy(true);
    try {
      onChange(await compressSquareImage(file));
    } catch {
      toast.error("Gambar tidak bisa diproses.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <label className="group relative block aspect-square w-full cursor-pointer overflow-hidden rounded-2xl border-2 border-dashed border-[#1A3323]/20 bg-[#F5F3EB]" data-testid="feed-image-picker">
      {value ? <img src={value} alt="Thumbnail" className="h-full w-full object-cover" data-testid="feed-image-preview" /> : (
        <span className="flex h-full flex-col items-center justify-center gap-2 text-sm text-muted-foreground"><ImagePlus className="h-8 w-8" /> Unggah gambar (dipotong 1:1)</span>
      )}
      {busy && <span className="absolute inset-0 flex items-center justify-center bg-white/70"><Loader2 className="h-6 w-6 animate-spin" /></span>}
      {value && <span className="absolute bottom-2 left-2 rounded-full bg-[#1A3323]/85 px-2 py-0.5 text-[11px] font-bold text-white" data-testid="feed-image-size">{dataUrlKb(value)} KB</span>}
      <input type="file" accept="image/*" className="hidden" onChange={(e) => pick(e.target.files[0])} data-testid="feed-image-input" />
    </label>
  );
}

function PostForm({ editing, onSaved, onCancel }) {
  const [v, setV] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setV(editing ? { ...EMPTY, ...editing, link: editing.link || "" } : EMPTY);
  }, [editing]);
  const submit = async (e) => {
    e.preventDefault();
    if (!v.image) return toast.error("Unggah gambar thumbnail dulu.");
    setBusy(true);
    try {
      const payload = { title: v.title, description: v.description, link: v.link || null, image: v.image };
      const { data } = editing ? await api.put(`/admin/feed/${editing.id}`, payload) : await api.post("/admin/feed", payload);
      toast.success(editing ? "Post diperbarui." : data.auto_deleted ? "Post dipublikasikan. Post terlama otomatis dihapus." : "Post dipublikasikan.");
      setV(EMPTY);
      onSaved(data.items);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-[200px_1fr]" data-testid="feed-form">
      <ImagePicker value={v.image} onChange={(image) => setV({ ...v, image })} />
      <div className="space-y-3">
        <Field label="Judul post"><Input required maxLength={100} value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} data-testid="feed-title-input" /></Field>
        <Field label={`Deskripsi (${v.description.length}/200)`}><Textarea rows={4} maxLength={200} value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} data-testid="feed-description-input" /></Field>
        <Field label="Link (opsional)" hint="Dibuka di tab baru, mis. link pendaftaran atau Instagram."><Input type="url" placeholder="https://" value={v.link} onChange={(e) => setV({ ...v, link: e.target.value })} data-testid="feed-link-input" /></Field>
        <div className="flex gap-2">
          <Button type="submit" disabled={busy} className="rounded-full" data-testid="feed-submit-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{editing ? "Simpan perubahan" : "Publikasikan"}</Button>
          {editing && <Button type="button" variant="ghost" onClick={onCancel} data-testid="feed-cancel-edit-button">Batal</Button>}
        </div>
      </div>
    </form>
  );
}

function PostRow({ post, onEdit, onDelete }) {
  return (
    <div className="flex gap-3 rounded-xl bg-[#F5F3EB] p-3" data-testid={`admin-feed-post-${post.id}`}>
      <img src={post.image} alt="" className="h-20 w-20 shrink-0 rounded-lg object-cover" />
      <div className="min-w-0 flex-1">
        <div className="font-bold text-[#1A3323]">{post.title}</div>
        <p className="line-clamp-2 text-xs text-muted-foreground">{post.description}</p>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
          {fmtDateTime(post.created_at)}
          {post.link && <a href={post.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 font-semibold text-[#3D6F4B]"><ExternalLink className="h-3 w-3" /> link</a>}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <Button size="icon" variant="ghost" onClick={() => onEdit(post)} data-testid={`edit-feed-${post.id}`}><Pencil className="h-4 w-4" /></Button>
        <Confirm testid={`delete-feed-${post.id}`} title="Hapus post?" desc={post.title} destructive confirmText="Hapus" onConfirm={() => onDelete(post)}
          trigger={<Button size="icon" variant="ghost" data-testid={`delete-feed-${post.id}-button`}><Trash2 className="h-4 w-4 text-red-600" /></Button>} />
      </div>
    </div>
  );
}

export default function FeedAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  useEffect(() => {
    api.get("/admin/feed").then((r) => setItems(r.data.items));
  }, []);
  const remove = async (post) => {
    try {
      setItems((await api.delete(`/admin/feed/${post.id}`)).data.items);
      toast.success("Post dihapus.");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="max-w-4xl">
      <PageTitle title="Feed Peserta" desc="Tampil di dashboard peserta. Maksimal 5 post: post ke-6 otomatis menghapus post terlama." />
      <div className="grid gap-4">
        <Panel title={editing ? "Edit post" : "Post baru"}><PostForm editing={editing} onSaved={(list) => { setItems(list); setEditing(null); }} onCancel={() => setEditing(null)} /></Panel>
        <Panel title={`Post aktif (${items.length}/5)`}>
          <div className="space-y-2" data-testid="admin-feed-list">
            {items.length === 0 && <p className="text-sm text-muted-foreground">Belum ada post.</p>}
            {items.map((p) => <PostRow key={p.id} post={p} onEdit={setEditing} onDelete={remove} />)}
          </div>
        </Panel>
      </div>
    </div>
  );
}
